import { useTranslation } from 'react-i18next';

import {
  isRecommendation,
  type ConsiderationStatus,
  type Recommendation,
} from '@/api/vocabularies';
import { cn } from '@/lib/cn';

import { statusDotStyles } from './statusDotStyles';

interface Props {
  value: ConsiderationStatus | Recommendation;
  className?: string;
}

/**
 * A badge is a shape **and** a word, always.
 *
 * Colour never carries meaning alone: roughly one in twelve men has a colour vision deficiency,
 * and this is a tool for reading other people's judgements — a misread recommendation is
 * expensive. The dot may be decorative because the word beside it carries the meaning; the word
 * may never be dropped.
 *
 * ⚠️ The two vocabularies come from the OpenAPI document (`@/api/vocabularies`), which has carried
 * `ConsiderationStatus` since Sprint 3 and `OverallRecommendation` since Sprint 4 — they were
 * hand-written unions here until TASK-061.
 *
 * ⚠️ **A value from outside them renders nothing rather than throwing, since TASK-104** — and the two
 * vocabularies stopped arriving by the same route in that release, which is the thing to be exact
 * about here. Both fields **were** plain `string` in the document, and client modules narrowed each at
 * the edge through a helper of its own:
 *
 *  * **the verdicts are declared now.** `VerdictCount.verdict`, `latest_recommendation` and
 *    `overall_recommendation` are the `OverallRecommendation` enum since backend TASK-104, so their
 *    helper `asRecommendation` lost every caller and BUG-038 deleted it — **that one, and only that
 *    one**. `isRecommendation` is untouched and is imported below: it chooses *which* catalogue names
 *    a value, which is a different question from whether the value is in the set.
 *  * **the status is not.** `PipelineSegment.status` is still `{"type": "string"}` in
 *    `openapi.snapshot.json` (the enum on the backend is still a Backlog item), so
 *    `asConsiderationStatus` in `@/api/vocabularies` is live and `@/api/dashboard` calls it on every
 *    pipeline payload. An unknown status stops there, before it reaches this component.
 *
 * ⚠️ **This paragraph said *"the narrowing is gone — helper and all"* until BUG-062, and that wording
 * is retracted rather than quietly rewritten.** It over-described a correction, which is the more
 * dangerous direction on this file: a reader who believes the narrowing is gone deletes a helper that
 * still has a caller, and this component is the one place where being wrong about that was fatal.
 *
 * `statusDotStyles[value]` for an unknown value is `undefined`, and reading `style.pill` off it throws
 * **inside a render**, which takes the whole screen to §8's error state over one badge. A badge that
 * silently does not appear is a bug; a screen that dies because of one is an outage, and the `Record`
 * plus `candidates.test.tsx`'s snapshot comparison are what catch the real cause at build time.
 */
export function StatusDot({ value, className }: Props) {
  const { t } = useTranslation('components');
  const style = statusDotStyles[value];
  if (style === undefined) return null;
  const label = isRecommendation(value) ? t(`recommendation.${value}`) : t(`status.${value}`);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm',
        style.pill && 'rounded px-2 py-0.5',
        style.pill,
        style.text,
        className,
      )}
    >
      {style.dot && <span aria-hidden className={cn('size-1.5 rounded-full', style.dot)} />}
      {label}
    </span>
  );
}
